import { DependencyList, RefObject, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

function setupReveals(root: HTMLElement) {
  const splits: SplitText[] = [];

  gsap.utils.toArray<HTMLElement>("[data-split]", root).forEach((element) => {
    const split = SplitText.create(element, {
      type: "lines",
      aria: "auto",
      linesClass: "premium-line",
    });
    splits.push(split);
    gsap.set(split.lines, {
      autoAlpha: 0,
      yPercent: 34,
      rotateX: -4,
      filter: "blur(8px)",
      transformOrigin: "50% 100%",
    });
    gsap.to(split.lines, {
      autoAlpha: 1,
      yPercent: 0,
      rotateX: 0,
      filter: "blur(0px)",
      duration: 0.95,
      stagger: 0.07,
      ease: "expo.out",
      scrollTrigger: {
        trigger: element,
        start: "top 86%",
        once: true,
      },
    });
  });

  gsap.utils.toArray<HTMLElement>("[data-reveal]", root).forEach((element) => {
    gsap.fromTo(
      element,
      { autoAlpha: 0, y: 24 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.82,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 90%",
          once: true,
        },
      },
    );
  });

  return () => splits.forEach((split) => split.revert());
}

function setupMicroInteractions(root: HTMLElement) {
  if (window.matchMedia("(pointer: coarse)").matches) return () => undefined;

  const cleanups: Array<() => void> = [];

  gsap.utils.toArray<HTMLElement>("[data-magnetic]", root).forEach((element) => {
    const xTo = gsap.quickTo(element, "x", { duration: 0.55, ease: "power3.out" });
    const yTo = gsap.quickTo(element, "y", { duration: 0.55, ease: "power3.out" });

    const move = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      const x = gsap.utils.clamp(-12, 12, (event.clientX - rect.left - rect.width / 2) * 0.22);
      const y = gsap.utils.clamp(-9, 9, (event.clientY - rect.top - rect.height / 2) * 0.22);
      xTo(x);
      yTo(y);
    };
    const leave = () => {
      xTo(0);
      yTo(0);
    };

    element.addEventListener("pointermove", move);
    element.addEventListener("pointerleave", leave);
    cleanups.push(() => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
    });
  });

  gsap.utils.toArray<HTMLElement>("[data-spotlight]", root).forEach((element) => {
    const move = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      element.style.setProperty("--spot-x", `${event.clientX - rect.left}px`);
      element.style.setProperty("--spot-y", `${event.clientY - rect.top}px`);
      element.dataset.spotlightActive = "true";
    };
    const leave = () => {
      element.dataset.spotlightActive = "false";
    };

    element.addEventListener("pointermove", move);
    element.addEventListener("pointerleave", leave);
    cleanups.push(() => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
    });
  });

  gsap.utils.toArray<HTMLElement>("[data-tilt]", root).forEach((element) => {
    const rotateX = gsap.quickTo(element, "rotationX", { duration: 0.6, ease: "power3.out" });
    const rotateY = gsap.quickTo(element, "rotationY", { duration: 0.6, ease: "power3.out" });
    const scaleX = gsap.quickTo(element, "scaleX", { duration: 0.6, ease: "power3.out" });
    const scaleY = gsap.quickTo(element, "scaleY", { duration: 0.6, ease: "power3.out" });

    const move = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      rotateX(py * -5);
      rotateY(px * 7);
      scaleX(1.012);
      scaleY(1.012);
    };
    const leave = () => {
      rotateX(0);
      rotateY(0);
      scaleX(1);
      scaleY(1);
    };

    gsap.set(element, { transformPerspective: 1000, transformStyle: "preserve-3d" });
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerleave", leave);
    cleanups.push(() => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
    });
  });

  return () => cleanups.forEach((cleanup) => cleanup());
}

export function useGsap(
  scope: RefObject<HTMLElement | null>,
  setup: (gsapInstance: typeof gsap) => void,
  deps: DependencyList = []
) {
  useLayoutEffect(() => {
    const root = scope.current;
    if (!root) return;

    root.dataset.motionReady = "true";
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      gsap.set(root.querySelectorAll("[data-reveal]"), { clearProps: "all" });
      return () => {
        delete root.dataset.motionReady;
      };
    }

    let cleanupReveals: () => void = () => {};
    let cleanupInteractions: () => void = () => {};
    const context = gsap.context(() => {
      setup(gsap);
      cleanupReveals = setupReveals(root);
      cleanupInteractions = setupMicroInteractions(root);
    }, root);

    document.fonts?.ready.then(() => ScrollTrigger.refresh());

    return () => {
      cleanupInteractions();
      cleanupReveals();
      context.revert();
      delete root.dataset.motionReady;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
